import { UsersIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";

/**
 * The shared "Team" affordance for team-visible Polly conversations (R10).
 *
 * One component renders in THREE places — the chat header (VS6), the
 * client-hub conversation row, and the Polly sidebar item (VS7) — so the
 * sites never drift on copy/icon/color. Binary by design: the caller decides
 * whether to render it; the component takes no data, no state, no hooks.
 *
 * Precedence (decided by the caller, not here): a team-visible row renders
 * this badge; a pure direct share renders the existing "Shared by {name}"
 * indicator; an owned row renders neither. A row that is both team-visible
 * and directly shared prefers "Team".
 *
 * Design (`.impeccable.md`): `variant="secondary"` on purpose — team
 * visibility is a *state* indicator, not a CTA. Indigo (`bg-primary`) is
 * reserved for selection; copper for CTAs; secondary is the correct, quieter
 * tier. The `UsersIcon` + the word "Team" keep it legible against the
 * plain-text "Shared by {name}" line and the secondary-palette client chip it
 * mirrors (the agent chip is indigo/primary and already distinct) — type
 * distinguishability over palette cohesion. No pulse on first enable (the
 * enable toast already signals "just happened"; the badge is a persistent
 * state), no tooltip, no member count. Inherits the Badge's `h-5`, so it
 * never raises row height.
 */
export interface TeamVisibleBadgeProps {
    /** Optional extra classes the caller merges onto the badge. */
    className?: string;
}

export function TeamVisibleBadge({ className }: TeamVisibleBadgeProps) {
    return (
        <Badge
            variant="secondary"
            className={className}
            data-testid="team-visible-badge"
        >
            <UsersIcon aria-hidden className="shrink-0" />
            Team
        </Badge>
    );
}
